import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';

import { useDeliveryProofForm } from '@/features/delivery-proof/hooks/useDeliveryProofForm';

import { ContractSelector, type ContractSelection } from '@/features/delivery-proof/components/ContractSelector';
import { SocietySelector } from '@/features/societies/components/SocietySelector';
import { UserSelector } from '@/features/contracts/components/form-fields/UserSelector';
import { useAuth } from '@/hooks/auth/useAuth';
import { useDeliveryProofStore } from '@/features/delivery-proof/stores/deliveryProofStore';
import { useAvailableSocieties } from '@/routes/outil-pro/hooks/societies/useAvailableSocieties';
import { EligibleSocietySelector } from '@/features/societies/components/EligibleSocietySelector';
import { FormInput } from '@/Components/shared/FormInput';
import type { WizardStepHandle } from '@/Components/shared/WizardLayout.types';

interface Step1ContractSelectionProps {
  isSocietyLocked?: boolean;
  onSelectionChange?: (selection: ContractSelection | null) => void;
  onDpNumberChange?: (dpNumber: string) => void;
}

export const Step1ContractSelection = forwardRef<WizardStepHandle, Step1ContractSelectionProps>(({ isSocietyLocked = false, onSelectionChange, onDpNumberChange }, ref) => {
  const [searchParams] = useSearchParams();
  const initialContractId = searchParams.get('contractId') ? parseInt(searchParams.get('contractId')!) : undefined;
  const { user } = useAuth();
  const isInternalUser = user?.type === 'INTERNAL';
  const isExternalUser = user?.type === 'EXTERNAL';
  const { societies: eligibleSocieties } = useAvailableSocieties('deliveryProof');
  const isExternalMultiSociety = isExternalUser && eligibleSocieties.length > 1;
  const { createDeliveryProof, nextStep, updateFormData } = useDeliveryProofForm();
  const formData = useDeliveryProofStore((state) => state.formData);
  const deliveryProofId = useDeliveryProofStore((state) => state.deliveryProofId);

  // Mode reprise : le DP existe déjà (chargé depuis dpId URL param)
  const isResuming = !!deliveryProofId;

  const [selection, setSelection] = useState<ContractSelection | null>(null);
  const [dpNumber, setDpNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pré-remplir dpNumber depuis le store en mode reprise
  useEffect(() => {
    if (isResuming && formData.dpNumber) {
      setDpNumber(formData.dpNumber);
    }
  }, [isResuming, formData.dpNumber]);

  // Auto-sélection société pour external user avec une seule société éligible
  useEffect(() => {
    if (
      isExternalUser &&
      !isSocietyLocked &&
      !formData.societyId &&
      eligibleSocieties.length === 1
    ) {
      updateFormData({ societyId: eligibleSocieties[0].societyId });
    }
  }, [isExternalUser, isSocietyLocked, formData.societyId, eligibleSocieties, updateFormData]);

  // Dérivation pure : societyId à utiliser pour filtrer les contrats
  const societyId = useMemo(() => {
    if (isInternalUser) return formData.societyId;
    if (isExternalUser) return formData.societyId || user?.societyId;
    return user?.societyId;
  }, [isInternalUser, isExternalUser, formData.societyId, user?.societyId]);

  // Reset selection quand societyId change (mode création uniquement)
  // Ne pas reset si un initialContractId est fourni (pré-remplissage depuis URL)
  useEffect(() => {
    if (!isResuming && !initialContractId) {
      setSelection(null);
      setDpNumber('');
    }
  }, [societyId, isResuming, initialContractId]);

  // Pré-remplir dpNumber et userId quand un contrat/brouillon est sélectionné
  const handleSelectionChange = (newSelection: ContractSelection | null) => {
    setSelection(newSelection);
    onSelectionChange?.(newSelection);
    const newDpNumber = newSelection?.type === 'contract' && newSelection.contract.existingDpNumber
      ? newSelection.contract.existingDpNumber
      : '';
    setDpNumber(newDpNumber);
    onDpNumberChange?.(newDpNumber);

    // Auto-extraire userId du contrat/brouillon sélectionné
    if (newSelection?.type === 'contract' && newSelection.contract.userId) {
      updateFormData({ userId: newSelection.contract.userId });
    } else if (newSelection?.type === 'draft' && newSelection.draft.userId) {
      updateFormData({ userId: newSelection.draft.userId });
    } else {
      updateFormData({ userId: undefined, userName: undefined });
    }
  };

  useImperativeHandle(ref, () => ({
    handleNext: () => handleSubmit(),
  }));

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);

    if (isResuming) {
      // En mode reprise, synchroniser dpNumber vers le store puis avancer
      updateFormData({ dpNumber: dpNumber || undefined });
      nextStep();
      return;
    }

    if (!selection) {
      setError('Veuillez sélectionner un contrat');
      return;
    }

    // Pour les utilisateurs INTERNAL, vérifier que société et utilisateur sont renseignés
    if (isInternalUser && (!formData.societyId || !formData.userId)) {
      setError(isSocietyLocked
        ? 'Le contrat sélectionné n\'a pas d\'utilisateur associé'
        : 'Veuillez sélectionner une société et un utilisateur');
      return;
    }

    // Pour les EXTERNAL multi-société, vérifier que la société est bien sélectionnée
    if (isExternalMultiSociety && !formData.societyId) {
      setError('Veuillez sélectionner une société');
      return;
    }

    try {
      const source = selection.type === 'contract'
        ? { contractNum: selection.contract.label }
        : { draftUuid: selection.draft.uuid };

      await createDeliveryProof(
        source,
        dpNumber || undefined,
        formData.societyId,
        formData.userId
      );
      nextStep();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-2 sm:tw-p-4">
      <div className="tw-p-3 sm:tw-p-6 tw-space-y-5">
      <div className="tw-flex tw-items-center tw-gap-2">
        <FileText className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
        <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Contrat & bon de commande</h4>
      </div>
      <form onSubmit={handleSubmit} className="tw-space-y-6">
            {/* Société et Utilisateur (uniquement pour INTERNAL en mode création, masqué si société verrouillée car userId est auto-extrait du contrat) */}
            {isInternalUser && !isResuming && !isSocietyLocked && (
              <div id="dp-society-user-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-3 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
                <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                  Sélectionnez la société et l'utilisateur pour lesquels créer l'attestation
                </p>
                {!isSocietyLocked && (
                  <SocietySelector
                    value={formData.societyId}
                    onChange={(societyId) => {
                      updateFormData({
                        societyId,
                        userId: undefined,
                        userName: undefined,
                      });
                    }}
                    required
                    filterActive
                    tooltip="Sélectionnez la société prescriptrice pour laquelle vous souhaitez créer cette attestation de livraison."
                  />
                )}
                {formData.societyId && (
                  <UserSelector
                    societyId={formData.societyId}
                    value={formData.userId}
                    onChange={(userId, userName) =>
                      updateFormData({
                        userId,
                        userName: typeof userName === 'string'
                          ? userName
                          : userName
                            ? `${userName.firstname} ${userName.lastname}`
                            : undefined
                      })
                    }
                    required
                    tooltip="Sélectionnez l'utilisateur rattaché à la société qui sera associé à cette attestation."
                  />
                )}
              </div>
            )}

            {/* Société - EXTERNAL multi-société : restreint aux sociétés autorisées */}
            {isExternalMultiSociety && !isResuming && !isSocietyLocked && (
              <div id="dp-society-external-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-3 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
                <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                  Sélectionnez la société émettrice de l'attestation
                </p>
                <EligibleSocietySelector
                  action="deliveryProof"
                  value={formData.societyId}
                  onChange={(uuid) => {
                    updateFormData({ societyId: uuid });
                    setSelection(null);
                    setDpNumber('');
                  }}
                  required
                  dataTestId="dp-external-society-selector"
                />
              </div>
            )}

            {/* Contrat : sélecteur en création, input disabled en reprise */}
            <div id="dp-contract-selector-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-3 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                Sélectionnez le contrat pour lequel créer l'attestation de livraison
              </p>
              {isResuming ? (
                <FormInput
                  label="Contrat"
                  value={formData.contractNum || (formData.draftUuid ? 'Brouillon contrat' : '-')}
                  onChange={() => {}}
                  disabled
                />
              ) : (
                <ContractSelector
                  value={selection}
                  onChange={handleSelectionChange}
                  societyId={societyId}
                  error={error && !selection ? error : undefined}
                  initialContractId={initialContractId}
                  tooltip="Seuls les contrats sans attestation en cours apparaissent dans cette liste."
                />
              )}
            </div>

            {/* Numéro de bon de commande */}
            <div id="dp-number-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-3 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                Numéro de bon de commande pour relier cette attestation à votre vente (optionnel)
              </p>
              <FormInput
                label="Numéro de bon de commande"
                value={dpNumber}
                onChange={(v: string) => { setDpNumber(v); onDpNumberChange?.(v); }}
                placeholder="Numéro de bon de commande"
                tooltip="Renseignez le numéro de bon de commande de votre vente pour faciliter le suivi. Ce champ est facultatif."
              />
            </div>

      </form>

      </div>
    </div>
  );
});

Step1ContractSelection.displayName = 'Step1ContractSelection';
